import DeployButton from './DeployButton';
import { useSelector } from 'react-redux';
import {
  selectDefaultCreationEnvironment,
  selectProductionCrmOrg,
  selectCrmOrgs,
} from '../../pages/environments/environmentsReducer';
import useCheckManagedPackage from '../install-managed-package/useCheckManagedPackage';
import { useState } from 'react';
import { CompleteSfSetupForDeployDialog } from './complete-sf-setup/CompleteSfSetupForDeployDialog';
import { useDeployButtonContext } from './DeployButtonContext';
import { useShouldRequestToDeploy } from './useShouldRequestToDeploy';
import { useIsSetupCompleted } from '../../../hooks/useIsSetupCompleted';
import useUserInfo from '../../../hooks/useUserInfo';
import { SweepNotificationVariant } from '../../../reducers/notificationsReducer';
import { useSweepNotifications } from '../../notifications/useSweepNotifications';
import pluralize from 'pluralize';
import { useAutomationsFormValidationCtx } from '../../Automations/automation-form-validation/AutomationsFormValidationCtx';

/**
 * Props for the ConnectedDeployButton component.
 */
export interface ConnectedDeployButtonProps {
  /**
   * Callback function triggered when the deploy or save button is clicked.
   * @param crmOrgIds - An array of CRM organization IDs. If the array is empty, the operation is a save. otherwise, it is a deploy.
   * @returns A promise that resolves when when the save is completed.
   */
  onDeployOrSave: (crmOrgIds: string[], deployAs?: AutomationDeployAs) => Promise<any>;

  /**
   * Specifies whether the button is disabled.
   */
  disabled?: boolean;

  /**
   * Callback function triggered when the diff is shown.
   */
  onShowDiff?: () => void;
}

const ConnectedDeployButton = ({
  onDeployOrSave,
  disabled,
  onShowDiff,
}: ConnectedDeployButtonProps) => {
  const { addNotification } = useSweepNotifications();
  const { checkIfUserHasPermissionsForOrg } = useUserInfo();
  const { entityNamePlural, entityName, permissions } = useDeployButtonContext();
  const productionEnvironment = useSelector(selectProductionCrmOrg);
  const defaultCreationEnvironment = useSelector(selectDefaultCreationEnvironment);
  const { getIsManagedPackagedInstalledWithRecheck } = useCheckManagedPackage();
  const [completeSfSetupDialog, setCompleteSfSetupDialog] = useState<{
    isOpen: boolean;
    crmOrgIdsToDeploy: string[];
    deployAs: AutomationDeployAs;
  }>();
  const canDeploy = useIsSetupCompleted();
  const crmOrgs = useSelector(selectCrmOrgs);

  const shouldRequestToDeploy = useShouldRequestToDeploy();
  const { defaultDeployType, deployAsFlowOption, invalidValidationReasons } =
    useAutomationsFormValidationCtx();
  const deployOrShowDialog = async (crmOrgIds: string[], deployAs: AutomationDeployAs) => {
    await getIsManagedPackagedInstalledWithRecheck({
      withPermissionsCheck: true,
    });

    if (canDeploy) {
      const allCrmOrgsWithPermissions = await Promise.all(
        crmOrgIds.map((id) => checkIfUserHasPermissionsForOrg(id, permissions.deploy)),
      );
      const orgsWithoutPermissions = crmOrgIds.filter(
        (orgId, idx) => !allCrmOrgsWithPermissions[idx],
      );

      if (!shouldRequestToDeploy && orgsWithoutPermissions.length > 0) {
        const _crmOrgs = crmOrgs
          .filter((crmOrg) => orgsWithoutPermissions.includes(crmOrg.id))
          ?.map((crmOrg) => crmOrg.name);
        const crmOrgNames = _crmOrgs.join(', ');

        addNotification({
          message: `You don't have permission to deploy ${entityNamePlural} to ${pluralize('environment', _crmOrgs?.length)} ${crmOrgNames}`,
          variant: SweepNotificationVariant.Error,
        });
        return;
      }
      await onDeployOrSave(crmOrgIds, deployAs);
    } else {
      setCompleteSfSetupDialog({
        isOpen: true,
        crmOrgIdsToDeploy: crmOrgIds,
        deployAs,
      });
    }
  };
  const _deployAsFlowOption = shouldRequestToDeploy ? undefined : deployAsFlowOption;

  return (
    <>
      <DeployButton
        defaultDeployType={defaultDeployType}
        deployAsFlowOption={_deployAsFlowOption}
        onShowDiff={onShowDiff}
        defaultCreationEnvironmentName={defaultCreationEnvironment?.name}
        prodEnvironmentName={productionEnvironment?.name}
        defaultIsProd={defaultCreationEnvironment?.id === productionEnvironment?.id}
        onDeployDefaultClick={
          defaultCreationEnvironment
            ? async (deployAs: AutomationDeployAs) =>
                await deployOrShowDialog([defaultCreationEnvironment.id], deployAs)
            : undefined
        }
        onDeployBothClick={
          defaultCreationEnvironment && productionEnvironment
            ? async (deployAs: AutomationDeployAs) =>
                await deployOrShowDialog(
                  [defaultCreationEnvironment.id, productionEnvironment.id],
                  deployAs,
                )
            : undefined
        }
        onSaveClick={async () => await onDeployOrSave([])}
        disabled={disabled}
        isRequestToDeploy={shouldRequestToDeploy}
        invalidValidationReasons={invalidValidationReasons}
      />
      {completeSfSetupDialog?.isOpen && (
        <CompleteSfSetupForDeployDialog
          cancelDialog={async () => {
            setCompleteSfSetupDialog(undefined);
          }}
          entityName={entityName}
          onCompleteItemsCallback={async () => {
            onDeployOrSave(completeSfSetupDialog.crmOrgIdsToDeploy, completeSfSetupDialog.deployAs);
            setCompleteSfSetupDialog(undefined);
          }}
          title={`Your ${entityName} was saved 🎉`}
        />
      )}
    </>
  );
};

export default ConnectedDeployButton;
